import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useToastsStore } from "@/stores/toastsStore";
import useMyFetch from "@stores/useMyFetch";
import { captureEvent } from "@/lib/analytics";

export const useSlideTemplatesStore = defineStore("slideTemplates", () => {
  const toast = useToastsStore();
  const serverTemplates = ref([]);
  const loading = ref(false);
  const error = ref(null);

  const currentThemeToken = ref(null);
  const allSlides = ref([]);
  const currentTemplateId = ref(null);

  const editedTemplates = ref({});
  const pendingNewTemplates = ref([]);
  const pendingDeletions = ref(new Set());
  const saving = ref(false);

  let tempIdCounter = -1;

  function getBasePath() {
    return currentThemeToken.value ? `/master_reports/${currentThemeToken.value}/slide_templates` : "/slide_templates";
  }

  function setThemeToken(themeToken) {
    currentThemeToken.value = themeToken;
  }

  const templates = computed(() => {
    const existing = serverTemplates.value.filter((t) => !pendingDeletions.value.has(t.id));
    const result = [...existing];

    for (const pending of pendingNewTemplates.value) {
      if (pending._insertAfter !== undefined && pending._insertAfter !== null) {
        const insertIndex = result.findIndex((t) => t.id === pending._insertAfter) + 1;
        result.splice(insertIndex, 0, pending);
      } else {
        result.push(pending);
      }
    }

    return result;
  });

  const currentTemplate = computed(() => {
    if (currentTemplateId.value === null) return null;
    return templates.value.find((t) => t.id === currentTemplateId.value);
  });

  const currentSourceSlide = computed(() => {
    if (!currentTemplateId.value) return null;
    const effective = getEffectiveTemplateData(currentTemplateId.value);
    if (!effective) return null;
    return allSlides.value.find((s) => s.index === effective.source_slide_index) ?? null;
  });

  const hasChanges = computed(() => {
    return (
      Object.keys(editedTemplates.value).length > 0 ||
      pendingNewTemplates.value.length > 0 ||
      pendingDeletions.value.size > 0
    );
  });

  function getEffectiveTemplateData(templateId) {
    const template = templates.value.find((t) => t.id === templateId);
    if (!template) return null;
    const edits = editedTemplates.value[templateId];
    if (!edits) return template;
    return { ...template, ...edits };
  }

  // surveyToken puts the request in the report-rendering context (survey-gated,
  // scoped to the survey account). Without it, the request is a super_admin
  // management call (master report editor).
  async function fetchTemplates(themeToken = null, reportThemeId = null, surveyToken = null) {
    if (themeToken) setThemeToken(themeToken);
    loading.value = true;
    error.value = null;

    const query = new URLSearchParams();
    if (surveyToken) query.set("survey_token", surveyToken);
    if (reportThemeId) query.set("report_theme_id", reportThemeId);
    const qs = query.toString();
    const path = qs ? `${getBasePath()}?${qs}` : getBasePath();
    const { response, data } = await useMyFetch(path).get().json();

    if (response.value.ok) {
      serverTemplates.value = data.value;
      pendingNewTemplates.value = [];
      pendingDeletions.value = new Set();
      editedTemplates.value = {};
    } else {
      error.value = data.value?.error || "Failed to fetch templates";
    }

    loading.value = false;
  }

  async function updateTemplateOnServer(id, params) {
    const template = serverTemplates.value.find((t) => t.id === id);
    if (!template) throw new Error("Template not found");

    const { response, data } = await useMyFetch(`${getBasePath()}/${template.token}`)
      .patch({ slide_template: params })
      .json();

    if (response.value.ok) {
      return data.value;
    } else {
      throw new Error(data.value?.error || "Failed to update template");
    }
  }

  async function createTemplateOnServer(templateData) {
    let position = null;
    if (templateData._insertAfter) {
      const originalTemplate = serverTemplates.value.find((t) => t.id === templateData._insertAfter);
      if (originalTemplate?.position != null) {
        position = originalTemplate.position + 1;
      }
    }

    const payload = {
      slide_template: {
        name: templateData.name,
        description: templateData.description || "",
        source_slide_index: templateData.source_slide_index,
        skin_type: templateData.skin_type || null,
        editable_fields: templateData.editable_fields || [],
        ...(position != null && { position }),
      },
    };

    const { response, data } = await useMyFetch(getBasePath()).post(payload).json();

    if (response.value.ok) {
      return data.value;
    } else {
      const errorMsg = data.value?.error || data.value?.errors?.join(", ") || "Failed to create template";
      throw new Error(errorMsg);
    }
  }

  async function deleteTemplateOnServer(id) {
    const template = serverTemplates.value.find((t) => t.id === id);
    if (!template) throw new Error("Template not found");

    const { response, data } = await useMyFetch(`${getBasePath()}/${template.token}`).delete().json();

    if (response.value.ok || response.value.status === 204 || response.value.status === 404) {
      return true;
    } else {
      const errorMsg = data.value?.error || data.value?.errors || "Failed to delete template";
      throw new Error(typeof errorMsg === "object" ? JSON.stringify(errorMsg) : errorMsg);
    }
  }

  async function loadAllSlides(pptxSourceId) {
    if (!pptxSourceId || !currentThemeToken.value) return;

    loading.value = true;
    error.value = null;

    try {
      const { response, data } = await useMyFetch(
        `/master_reports/${currentThemeToken.value}/pptx_imports/${pptxSourceId}`,
      )
        .get()
        .json();

      if (response.value.ok) {
        allSlides.value = data.value.slides || [];
      } else {
        error.value = data.value?.error || "Failed to load slides";
      }
    } catch (e) {
      error.value = e.message || "Failed to load slides";
    } finally {
      loading.value = false;
    }
  }

  function selectTemplate(templateId) {
    currentTemplateId.value = templateId;
  }

  function updateLocalTemplate(templateId, field, value) {
    const pendingIndex = pendingNewTemplates.value.findIndex((t) => t.id === templateId);
    if (pendingIndex !== -1) {
      pendingNewTemplates.value[pendingIndex][field] = value;
      return;
    }

    if (!editedTemplates.value[templateId]) {
      editedTemplates.value[templateId] = {};
    }
    editedTemplates.value[templateId][field] = value;
  }

  function addBlankTemplate() {
    const tempId = tempIdCounter--;
    const newTemplate = {
      id: tempId,
      name: "New template",
      description: "",
      source_slide_index: null,
      editable_fields: [],
      _isNew: true,
    };
    pendingNewTemplates.value.push(newTemplate);
    return newTemplate;
  }

  function removeTemplate(templateId) {
    const pendingIndex = pendingNewTemplates.value.findIndex((t) => t.id === templateId);
    if (pendingIndex !== -1) {
      pendingNewTemplates.value.splice(pendingIndex, 1);
    } else {
      pendingDeletions.value.add(templateId);
    }

    if (currentTemplateId.value === templateId) {
      const remaining = templates.value.filter((t) => t.id !== templateId);
      currentTemplateId.value = remaining.length > 0 ? remaining[0].id : null;
    }
  }

  async function saveAllChanges() {
    if (!hasChanges.value) return true;

    saving.value = true;
    error.value = null;

    try {
      const currentWasTempId = currentTemplateId.value < 0;
      let newIdForSelection = null;

      const deletionIds = [...pendingDeletions.value];
      await Promise.all(deletionIds.map((templateId) => deleteTemplateOnServer(templateId)));
      pendingDeletions.value = new Set();

      const pendingToCreate = [...pendingNewTemplates.value];
      if (pendingToCreate.length > 0) {
        const creationResults = await Promise.all(
          pendingToCreate.map(async (pendingTemplate) => {
            const created = await createTemplateOnServer(pendingTemplate);
            return { tempId: pendingTemplate.id, created };
          }),
        );

        if (currentWasTempId) {
          const createdMatch = creationResults.find((c) => c.tempId === currentTemplateId.value);
          if (createdMatch) {
            newIdForSelection = createdMatch.created.id;
          }
        }
      }
      pendingNewTemplates.value = [];

      const editsToSave = Object.entries(editedTemplates.value);
      if (editsToSave.length > 0) {
        await Promise.all(
          editsToSave.map(async ([templateId, changes]) => {
            await updateTemplateOnServer(parseInt(templateId), changes);
          }),
        );
      }
      editedTemplates.value = {};

      const { response, data } = await useMyFetch(getBasePath()).get().json();
      if (response.value.ok) {
        serverTemplates.value = data.value;
      }

      if (newIdForSelection) {
        currentTemplateId.value = newIdForSelection;
      }

      captureEvent("slide_templates_saved", {
        templates_created: pendingToCreate.length,
        templates_deleted: deletionIds.length,
        templates_updated: editsToSave.length,
      });

      return true;
    } catch (e) {
      error.value = e.message || "Failed to save changes";
      toast.error("Failed to save changes");
      return false;
    } finally {
      saving.value = false;
    }
  }

  function reorderTemplates(orderedIds) {
    const serverIds = orderedIds.filter((id) => id > 0);
    const pendingIds = orderedIds.filter((id) => id < 0);

    const reorderedServer = serverIds.map((id) => serverTemplates.value.find((t) => t.id === id)).filter(Boolean);
    serverTemplates.value = reorderedServer;

    serverIds.forEach((id, index) => {
      const newPosition = index + 1;
      if (!editedTemplates.value[id]) {
        editedTemplates.value[id] = {};
      }
      editedTemplates.value[id].position = newPosition;
    });

    pendingIds.forEach((id, index) => {
      const pendingTemplate = pendingNewTemplates.value.find((t) => t.id === id);
      if (pendingTemplate) {
        pendingTemplate.position = serverIds.length + index + 1;
        delete pendingTemplate._insertAfter;
      }
    });
  }

  function clearEditState() {
    currentTemplateId.value = null;
    allSlides.value = [];
    editedTemplates.value = {};
    pendingNewTemplates.value = [];
    pendingDeletions.value = new Set();
  }

  function findTemplateForSlide(slide) {
    if (!slide?.slide_template_id) return null;
    return templates.value.find((t) => t.id === slide.slide_template_id) || slide.slide_template || null;
  }

  return {
    templates,
    loading,
    error,
    currentThemeToken,
    allSlides,
    currentTemplateId,
    currentTemplate,
    currentSourceSlide,
    hasChanges,
    saving,
    findTemplateForSlide,
    fetchTemplates,
    setThemeToken,
    loadAllSlides,
    selectTemplate,
    addBlankTemplate,
    reorderTemplates,
    removeTemplate,
    clearEditState,
    getEffectiveTemplateData,
    updateLocalTemplate,
    saveAllChanges,
  };
});
